// Client Momentum Score — likelihood-to-proceed signal (separate from file readiness).
import React from 'react';
import { Flame, TrendingUp, TrendingDown, Snowflake, ArrowUpRight, ArrowDownRight, Gauge } from 'lucide-react';

export const BANDS = {
  hot: { icon: Flame, label: 'Hot', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500', bar: 'bg-rose-500' },
  warm: { icon: TrendingUp, label: 'Warm', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  cooling: { icon: TrendingDown, label: 'Cooling', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-400', bar: 'bg-sky-400' },
  cold: { icon: Snowflake, label: 'Cold', bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', dot: 'bg-slate-300', bar: 'bg-slate-400' },
};

const cfg = (m) => BANDS[m?.band] || BANDS.cold;

// Compact pill for list rows / cards.
export function MomentumBadge({ momentum, showLabel = true, testId }) {
  if (!momentum) return null;
  const c = cfg(momentum);
  const Icon = c.icon;
  return (
    <span
      data-testid={testId}
      title={(momentum.why || []).join(' · ') || `Momentum ${momentum.score}`}
      className={`inline-flex items-center gap-1 rounded-full border ${c.border} ${c.bg} ${c.text} px-2 py-0.5 text-[11px] font-black tabular-nums whitespace-nowrap`}
    >
      <Icon className="w-3 h-3" />
      {momentum.score}
      {showLabel && <span className="font-bold uppercase tracking-wide text-[9px] opacity-80">{momentum.band_label || c.label}</span>}
    </span>
  );
}

// Full breakdown card for the client file.
export function MomentumPanel({ momentum, testId = 'obd-momentum' }) {
  if (!momentum) return null;
  const c = cfg(momentum);
  const Icon = c.icon;
  return (
    <div data-testid={testId} className={`rounded-2xl border ${c.border} bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)]`}>
      <div className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 ${c.bg}`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`grid place-items-center h-9 w-9 rounded-xl ${c.text} bg-white/70 shrink-0`}><Icon className="w-4.5 h-4.5" /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 flex items-center gap-1"><Gauge className="w-3 h-3" /> Client Momentum</p>
            <p className={`text-sm font-black ${c.text}`}>{momentum.band_label || c.label} · likelihood to proceed</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-3xl font-black tabular-nums leading-none ${c.text}`} data-testid={`${testId}-score`}>{momentum.score}</p>
          <p className="text-[10px] font-semibold text-slate-400">/ 100</p>
        </div>
      </div>
      <div className="px-4 sm:px-5 pt-3">
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${c.bar} transition-[width] duration-700`} style={{ width: `${momentum.score}%` }} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-1.5 px-4 sm:px-5 py-4">
        <div className="space-y-1.5">
          {(momentum.positives || []).length ? (momentum.positives || []).map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[12px] text-slate-700"><ArrowUpRight className="w-3.5 h-3.5 text-emerald-500 shrink-0" /><span className="flex-1 min-w-0">{s.label}</span><b className="text-emerald-600 tabular-nums">+{s.points}</b></div>
          )) : <p className="text-[11px] text-slate-400">No positive signals yet.</p>}
        </div>
        <div className="space-y-1.5">
          {(momentum.negatives || []).length ? (momentum.negatives || []).map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[12px] text-slate-700"><ArrowDownRight className="w-3.5 h-3.5 text-rose-500 shrink-0" /><span className="flex-1 min-w-0">{s.label}</span><b className="text-rose-600 tabular-nums">{s.points}</b></div>
          )) : <p className="text-[11px] text-slate-400">Nothing dragging it down.</p>}
        </div>
      </div>
      <p className="px-4 sm:px-5 pb-3.5 -mt-1 text-[10px] text-slate-400">Behaviour-based — separate from file readiness. Spend time on the hottest first.</p>
    </div>
  );
}

export default MomentumBadge;
